import { getApiUrl, getUserId, headerBuilder } from '../api/config';
import { PlanDataService } from './PlanDataService';
import { MPlanData, ParsedPlanApprovalRequest, StreamingPlanUpdate, StreamMessage, WebsocketMessageType } from '../models';
import { 
  validateWebSocketMessage, 
  DataSanitizer, 
  TypeGuards, 
  createErrorContext,
  type SafeAgentMessageData 
} from '../utils/agentMessageValidator';
import { ErrorLogger } from '../utils/ErrorLogger';
import { safeGet, typeSafeAccess, SafePropertyAccess } from '../utils/safePropertyAccess';


class WebSocketService {
    private ws: WebSocket | null = null;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectDelay = 12000;
    private listeners: Map<string, Set<(message: StreamMessage) => void>> = new Map();
    private planSubscriptions: Set<string> = new Set();
    private reconnectTimer: NodeJS.Timeout | null = null;
    private isConnecting = false;
    
    // Enhanced connection status tracking
    private connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'failed' = 'disconnected';
    private lastConnectionError: Error | null = null;
    private messageQueue: any[] = [];
    private maxQueueSize = 100;
    
    // Message validation and processing
    private validationEnabled = true;
    private sanitizationEnabled = true;
    private messageProcessingOrder: string[] = [];


    private buildSocketUrl(processId?: string, planId?: string): string {
        const baseWsUrl = getApiUrl() || 'ws://localhost:8000';
        // Trim and remove trailing slashes
        let base = (baseWsUrl || '').trim().replace(/\/+$/, '');
        
        // Handle relative URLs (e.g., "/api") by prepending the current host with ws/wss protocol
        if (base.startsWith('/')) {
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            base = `${protocol}//${window.location.host}${base}`;
        }
        
        // Normalize protocol: http -> ws, https -> wss
        base = base.replace(/^http:\/\//i, 'ws://')
            .replace(/^https:\/\//i, 'wss://');

        // Leave ws/wss as-is; anything else is assumed already correct

        // Decide path addition
        let userId = getUserId();
        // Use default_user to match backend expectations
        const finalUserId = (userId && userId !== "00000000-0000-0000-0000-000000000000") ? userId : "default_user";
        const hasApiSegment = /\/api(\/|$)/i.test(base);
        
        // If no planId or processId provided, use simple WebSocket endpoint
        if (!planId && !processId) {
            const socketPath = hasApiSegment ? '/v3/ws' : '/api/v3/ws';
            const url = `${base}${socketPath}?user_id=${finalUserId}`;
            console.log("Constructed simple WebSocket URL:", url);
            return url;
        }
        
        // Use plan-specific endpoint
        const socketPath = hasApiSegment ? '/v3/socket' : '/api/v3/socket';
        const url = `${base}${socketPath}${processId ? `/${processId}` : `/${planId}`}?user_id=${finalUserId}`;
        console.log("Constructed WebSocket URL:", url);
        return url;
    }
    connect(planId?: string, processId?: string): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.isConnecting) {
                reject(new Error('Connection already in progress'));
                return;
            }
            if (this.ws?.readyState === WebSocket.OPEN) {
                resolve();
                return;
            }
            try {
                this.isConnecting = true;
                const wsUrl = this.buildSocketUrl(processId, planId);
                console.log('🔌 WebSocket connecting to URL:', wsUrl);
                console.log('🔌 WebSocket planId:', planId);
                console.log('🔌 WebSocket processId:', processId);
                this.ws = new WebSocket(wsUrl);

                this.ws.onopen = () => {
                    this.isConnecting = false;
                    this.connectionStatus = 'connected';
                    this.reconnectAttempts = 0;
                    this.lastConnectionError = null;
                    
                    if (this.reconnectTimer) {
                        clearTimeout(this.reconnectTimer);
                        this.reconnectTimer = null;
                    }
                    
                    // Process queued messages
                    this.processQueuedMessages();
                    
                    this.emit('connection_status', { 
                        connected: true, 
                        status: this.connectionStatus,
                        reconnectAttempts: this.reconnectAttempts 
                    });
                    
                    ErrorLogger.getInstance().logInfo(
                        'WebSocketService',
                        'WebSocket connection established',
                        { reconnectAttempts: this.reconnectAttempts },
                        'connect'
                    );
                    
                    resolve();
                };

                this.ws.onmessage = (event) => {
                    this.handleIncomingMessage(event);
                };

                this.ws.onclose = (event) => {
                    this.isConnecting = false;
                    this.ws = null;
                    
                    // Update connection status based on close reason
                    if (event.code === 1000) {
                        this.connectionStatus = 'disconnected'; // Normal closure
                    } else {
                        this.connectionStatus = 'failed';
                        this.lastConnectionError = new Error(`WebSocket closed with code ${event.code}: ${event.reason}`);
                    }
                    
                    this.emit('connection_status', { 
                        connected: false, 
                        status: this.connectionStatus,
                        error: this.lastConnectionError?.message,
                        code: event.code,
                        reason: event.reason
                    });
                    
                    ErrorLogger.getInstance().logWarning(
                        'WebSocketService',
                        'WebSocket connection closed',
                        {
                            code: event.code,
                            reason: event.reason,
                            wasClean: event.wasClean
                        },
                        'onclose'
                    );
                    
                    // Attempt reconnection for non-normal closures
                    if (this.reconnectAttempts < this.maxReconnectAttempts && event.code !== 1000) {
                        this.attemptReconnect();
                    }
                };

                this.ws.onerror = (error) => {
                    this.isConnecting = false;
                    this.connectionStatus = 'failed';
                    this.lastConnectionError = new Error('WebSocket connection error');
                    
                    ErrorLogger.getInstance().logError(
                        'WebSocketService',
                        this.lastConnectionError,
                        {
                            reconnectAttempts: this.reconnectAttempts,
                            originalData: error
                        },
                        'onerror'
                    );
                    
                    if (this.reconnectAttempts === 0) {
                        reject(this.lastConnectionError);
                    }
                    
                    this.emit('error', { 
                        error: 'WebSocket connection error',
                        status: this.connectionStatus,
                        reconnectAttempts: this.reconnectAttempts
                    });
                };
            } catch (error) {
                this.isConnecting = false;
                reject(error);
            }
        });
    }

    disconnect(): void {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        this.reconnectAttempts = this.maxReconnectAttempts;
        if (this.ws) {
            this.ws.close(1000, 'Manual disconnect');
            this.ws = null;
        }
        this.planSubscriptions.clear();
        this.isConnecting = false;
    }

    subscribeToPlan(planId: string): void {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            const message = { type: 'subscribe_plan', plan_id: planId };
            this.ws.send(JSON.stringify(message));
            this.planSubscriptions.add(planId);
        }
    }

    unsubscribeFromPlan(planId: string): void {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            const message = { type: 'unsubscribe_plan', plan_id: planId };
            this.ws.send(JSON.stringify(message));
            this.planSubscriptions.delete(planId);
        }
    }

    on(eventType: string, callback: (message: StreamMessage) => void): () => void {
        console.log(`📥 ON: Registering listener for ${eventType}`);
        if (!this.listeners.has(eventType)) {
            this.listeners.set(eventType, new Set());
        }
        this.listeners.get(eventType)!.add(callback);
        console.log(`📥 ON: Listener registered for ${eventType}, total listeners: ${this.listeners.get(eventType)!.size}`);
        return () => {
            console.log(`📥 OFF: Unregistering listener for ${eventType}`);
            const setRef = this.listeners.get(eventType);
            if (setRef) {
                setRef.delete(callback);
                console.log(`📥 OFF: Listener unregistered for ${eventType}, remaining listeners: ${setRef.size}`);
                if (setRef.size === 0) this.listeners.delete(eventType);
            }
        };
    }

    off(eventType: string, callback: (message: StreamMessage) => void): void {
        const setRef = this.listeners.get(eventType);
        if (setRef) {
            setRef.delete(callback);
            if (setRef.size === 0) this.listeners.delete(eventType);
        }
    }

    onConnectionChange(callback: (connected: boolean) => void): () => void {
        return this.on('connection_status', (message: StreamMessage) => {
            callback(message.data?.connected || false);
        });
    }

    onStreamingMessage(callback: (message: StreamingPlanUpdate) => void): () => void {
        return this.on(WebsocketMessageType.AGENT_MESSAGE, (message: StreamMessage) => {
            if (message.data) callback(message.data);
        });
    }

    onPlanApprovalRequest(callback: (approvalRequest: ParsedPlanApprovalRequest) => void): () => void {
        return this.on(WebsocketMessageType.PLAN_APPROVAL_REQUEST, (message: StreamMessage) => {
            if (message.data) callback(message.data);
        });
    }

    onPlanApprovalResponse(callback: (response: any) => void): () => void {
        return this.on(WebsocketMessageType.PLAN_APPROVAL_RESPONSE, (message: StreamMessage) => {
            if (message.data) callback(message.data);
        });
    }

    private emit(eventType: string, data: any): void {
        const message: StreamMessage = {
            type: eventType as any,
            data,
            timestamp: new Date().toISOString()
        };
        const setRef = this.listeners.get(eventType);
        console.log(`📤 EMIT: eventType=${eventType}, listeners=${setRef?.size || 0}`);
        if (setRef) {
            setRef.forEach(cb => {
                try { 
                    console.log(`📤 EMIT: Calling listener for ${eventType}`);
                    cb(message); 
                    console.log(`📤 EMIT: Listener called successfully for ${eventType}`);
                } catch (e) { 
                    console.error('Listener error:', e); 
                }
            });
        } else {
            console.log(`📤 EMIT: No listeners registered for ${eventType}`);
        }
    }

    /**
     * Enhanced message handling with validation and sanitization
     */
    private handleIncomingMessage(event: MessageEvent): void {
        try {
            // Step 1: Parse JSON with error handling
            let rawMessage: any;
            try {
                rawMessage = JSON.parse(event.data);
            } catch (parseError) {
                ErrorLogger.getInstance().logError(
                    'WebSocketService',
                    new Error('Failed to parse WebSocket message JSON'),
                    { originalData: event.data?.substring(0, 500) },
                    'handleIncomingMessage'
                );
                return;
            }

            console.log("📨 WebSocket message received:", rawMessage);
            console.log("🔍 Message type:", rawMessage.type);
            console.log("🔍 Message type matches AGENT_MESSAGE:", rawMessage.type === WebsocketMessageType.AGENT_MESSAGE);
            console.log("🔍 WebsocketMessageType.AGENT_MESSAGE value:", WebsocketMessageType.AGENT_MESSAGE);
            console.log("🔍 Current AGENT_MESSAGE listeners:", this.listeners.get(WebsocketMessageType.AGENT_MESSAGE)?.size || 0);

            // Step 2: Validate message structure before processing
            if (this.validationEnabled) {
                const validation = validateWebSocketMessage(rawMessage);
                console.log("🔍 Validation result:", validation.isValid, validation.errors);
                if (!validation.isValid) {
                    ErrorLogger.getInstance().logWarning(
                        'WebSocketService',
                        'WebSocket message validation failed',
                        {
                            validationErrors: validation.errors,
                            originalData: rawMessage
                        },
                        'handleIncomingMessage'
                    );
                    
                    // Try to process anyway with sanitized data if possible
                    if (validation.normalizedMessage) {
                        rawMessage = validation.normalizedMessage;
                        console.log("🔍 Using normalized message after validation failure");
                    } else {
                        console.log("🔍 Skipping message - no normalized message available");
                        return; // Skip invalid messages
                    }
                }
            }

            // Step 3: Sanitize message data if enabled
            if (this.sanitizationEnabled) {
                rawMessage = this.sanitizeMessageData(rawMessage);
            }

            // Step 4: Track message processing order for validation
            this.messageProcessingOrder.push(`${rawMessage.type}-${Date.now()}`);
            if (this.messageProcessingOrder.length > 100) {
                this.messageProcessingOrder = this.messageProcessingOrder.slice(-50);
            }

            // Step 5: Process the validated and sanitized message
            this.handleMessage(rawMessage);

        } catch (error) {
            ErrorLogger.getInstance().logError(
                'WebSocketService',
                error instanceof Error ? error : new Error(String(error)),
                { originalData: event.data },
                'handleIncomingMessage'
            );
        }
    }

    /**
     * Sanitize message data to prevent XSS and normalize values
     */
    private sanitizeMessageData(message: any): any {
        if (!message || typeof message !== 'object') {
            return message;
        }

        const sanitized = { ...message };

        // Sanitize common string fields using safe property access
        const agentName = typeSafeAccess.string(sanitized, 'agent', '');
        if (agentName) {
            sanitized.agent = DataSanitizer.sanitizeAgentName(agentName);
        }

        const content = typeSafeAccess.string(sanitized, 'content', '');
        if (content) {
            sanitized.content = DataSanitizer.sanitizeContent(content);
        }

        // Sanitize nested data objects
        const data = safeGet(sanitized, 'data', null);
        if (data && typeof data === 'object') {
            const dataAgentName = typeSafeAccess.string(data, 'agent_name', '');
            if (dataAgentName) {
                sanitized.data.agent_name = DataSanitizer.sanitizeAgentName(dataAgentName);
            }
            
            const dataContent = typeSafeAccess.string(data, 'content', '');
            if (dataContent) {
                sanitized.data.content = DataSanitizer.sanitizeContent(dataContent);
            }
        }

        return sanitized;
    }

    /**
     * Process queued messages when connection is restored
     */
    private processQueuedMessages(): void {
        if (this.messageQueue.length === 0) {
            return;
        }

        ErrorLogger.getInstance().logInfo(
            'WebSocketService',
            `Processing ${this.messageQueue.length} queued messages`,
            {},
            'processQueuedMessages'
        );

        const messages = [...this.messageQueue];
        this.messageQueue = [];

        for (const message of messages) {
            try {
                this.send(message);
            } catch (error) {
                ErrorLogger.getInstance().logError(
                    'WebSocketService',
                    error instanceof Error ? error : new Error(String(error)),
                    { originalData: message },
                    'processQueuedMessages'
                );
            }
        }
    }

    /**
     * Queue message for sending when connection is restored
     */
    private queueMessage(message: any): void {
        if (this.messageQueue.length >= this.maxQueueSize) {
            // Remove oldest message to make room
            this.messageQueue.shift();
            ErrorLogger.getInstance().logWarning(
                'WebSocketService',
                'Message queue full, removing oldest message',
                {},
                'queueMessage'
            );
        }
        
        this.messageQueue.push(message);
    }

    private handleMessage(message: StreamMessage): void {

        switch (message.type) {
            case WebsocketMessageType.PLAN_APPROVAL_REQUEST: {
                console.log("🔔 PLAN_APPROVAL_REQUEST received:", message);
                console.log("🔍 Message data structure:", JSON.stringify(message.data, null, 2));
                
                const parsedData = PlanDataService.parsePlanApprovalRequest(message.data);
                console.log("📋 Parsed plan data:", parsedData);
                
                if (parsedData) {
                    const structuredMessage: ParsedPlanApprovalRequest = {
                        type: WebsocketMessageType.PLAN_APPROVAL_REQUEST,
                        plan_id: parsedData.id,
                        parsedData,
                        rawData: message.data
                    };
                    console.log("✅ Emitting structured plan approval request:", structuredMessage);
                    this.emit(WebsocketMessageType.PLAN_APPROVAL_REQUEST, structuredMessage);
                } else {
                    console.error("❌ Failed to parse plan approval request data:", message.data);
                    this.emit('error', { error: 'Failed to parse plan approval request' });
                }
                break;
            }

            case WebsocketMessageType.AGENT_MESSAGE: {
                console.log("🔔 AGENT_MESSAGE received in WebSocketService:", message);
                console.log("🔔 AGENT_MESSAGE listeners count:", this.listeners.get(WebsocketMessageType.AGENT_MESSAGE)?.size || 0);
                if (message.data) {
                    console.log('WebSocket message received:', message);
                    console.log('WebSocket message.data:', message.data);
                    console.log('WebSocket message.data type:', typeof message.data);
                    console.log('WebSocket message.data.agent_name:', message.data?.agent_name);
                    console.log('WebSocket message.data.content:', message.data?.content?.substring(0, 100));
                    
                    // Validate data structure before parsing
                    if (this.validationEnabled && !this.validateAgentMessageData(message.data)) {
                        ErrorLogger.getInstance().logWarning(
                            'WebSocketService',
                            'Agent message data validation failed',
                            {
                                messageType: 'AGENT_MESSAGE',
                                originalData: message.data
                            },
                            'handleMessage'
                        );
                        console.log('❌ Agent message validation failed:', message.data);
                        console.log('❌ Validation details - agent_name:', message.data?.agent_name, 'content:', message.data?.content);
                        return;
                    }
                    
                    console.log('✅ Agent message validation passed, parsing...');
                    const transformed = PlanDataService.parseAgentMessage(message);
                    console.log('Transformed AGENT_MESSAGE:', transformed);
                    
                    if (transformed) {
                        console.log('✅ Successfully parsed agent message, emitting:', transformed);
                        console.log('✅ Parsed agent name:', transformed.agent);
                        console.log('✅ Parsed content:', transformed.content?.substring(0, 100));
                        console.log('✅ Current listeners for AGENT_MESSAGE:', this.listeners.get(WebsocketMessageType.AGENT_MESSAGE)?.size || 0);
                        this.emit(WebsocketMessageType.AGENT_MESSAGE, transformed);
                        console.log('✅ Emit completed for AGENT_MESSAGE');
                    } else {
                        console.log('❌ Failed to parse agent message');
                        ErrorLogger.getInstance().logError(
                            'WebSocketService',
                            new Error('Failed to parse agent message'),
                            {
                                messageType: 'AGENT_MESSAGE',
                                originalData: message
                            },
                            'handleMessage'
                        );
                    }
                } else {
                    console.log('❌ AGENT_MESSAGE has no data:', message);
                }
                break;
            }

            case WebsocketMessageType.AGENT_MESSAGE_STREAMING: {
                console.log("Message streamming agent buffer:", message);
                if (message.data) {
                    // Validate streaming message data structure
                    if (this.validationEnabled && !this.validateStreamingMessageData(message.data)) {
                        ErrorLogger.getInstance().logWarning(
                            'WebSocketService',
                            'Streaming message data validation failed',
                            {
                                messageType: 'AGENT_MESSAGE_STREAMING',
                                originalData: message.data
                            },
                            'handleMessage'
                        );
                        return;
                    }
                    
                    const streamedMessage = PlanDataService.parseAgentMessageStreaming(message);
                    console.log('WebSocket AGENT_MESSAGE_STREAMING message received:', streamedMessage);
                    
                    if (streamedMessage) {
                        this.emit(WebsocketMessageType.AGENT_MESSAGE_STREAMING, streamedMessage);
                    } else {
                        ErrorLogger.getInstance().logError(
                            'WebSocketService',
                            new Error('Failed to parse streaming agent message'),
                            {
                                messageType: 'AGENT_MESSAGE_STREAMING',
                                originalData: message
                            },
                            'handleMessage'
                        );
                    }
                }
                break;
            }

            case WebsocketMessageType.USER_CLARIFICATION_REQUEST: {
                console.log("Message clarification':", message);
                if (message.data) {
                    const transformed = PlanDataService.parseUserClarificationRequest(message);
                    console.log('WebSocket USER_CLARIFICATION_REQUEST message received:', transformed);
                    this.emit(WebsocketMessageType.USER_CLARIFICATION_REQUEST, transformed);
                }
                break;
            }


            case WebsocketMessageType.AGENT_TOOL_MESSAGE: {
                console.log("Message agent tool':", message);
                if (message.data) {
                    //const transformed = PlanDataService.parseUserClarificationRequest(message);
                    this.emit(WebsocketMessageType.AGENT_TOOL_MESSAGE, message);
                }
                break;
            }

            case WebsocketMessageType.USER_CLARIFICATION_RESPONSE:
            case WebsocketMessageType.REPLAN_APPROVAL_REQUEST:
            case WebsocketMessageType.REPLAN_APPROVAL_RESPONSE:
            case WebsocketMessageType.PLAN_APPROVAL_RESPONSE:
            case WebsocketMessageType.AGENT_STREAM_START:
            case WebsocketMessageType.AGENT_STREAM_END:
            case WebsocketMessageType.SYSTEM_MESSAGE: {
                console.log("Message other types':", message);
                this.emit(message.type, message);
                break;
            }
            
            case WebsocketMessageType.WORKFLOW_COMPLETED: {
                console.log("Workflow completed message received:", message);
                this.emit(WebsocketMessageType.WORKFLOW_COMPLETED, message);
                break;
            }

            case WebsocketMessageType.AGENT_RESULT: {
                console.log("🔔 AGENT_RESULT received:", message);
                // Agent result contains final results from agent execution
                // Emit it so listeners can process the result data
                if (message.data) {
                    this.emit(WebsocketMessageType.AGENT_RESULT, message);
                }
                break;
            }

            default: {
                // Handle ping messages and other unknown message types
                if ((message as any).type === "ping") {
                    // Respond to server ping with pong
                    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                        this.ws.send(JSON.stringify({
                            type: "pong",
                            timestamp: new Date().toISOString()
                        }));
                    }
                } else {
                    console.log("Message default':", message);
                    this.emit(message.type, message);
                }
                break;
            }
        }
    }

    private attemptReconnect(): void {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.connectionStatus = 'failed';
            ErrorLogger.getInstance().logError(
                'WebSocketService',
                new Error('Max reconnection attempts reached'),
                { reconnectAttempts: this.reconnectAttempts },
                'attemptReconnect'
            );
            
            this.emit('error', { 
                error: 'Max reconnection attempts reached',
                status: this.connectionStatus,
                reconnectAttempts: this.reconnectAttempts
            });
            return;
        }
        
        if (this.isConnecting || this.reconnectTimer) return;
        
        this.connectionStatus = 'reconnecting';
        this.reconnectAttempts++;
        
        // Exponential backoff with jitter
        const baseDelay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
        const jitter = Math.random() * 1000; // Add up to 1 second of jitter
        const delay = Math.min(baseDelay + jitter, 60000); // Cap at 60 seconds
        
        ErrorLogger.getInstance().logInfo(
            'WebSocketService',
            `Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`,
            {
                delay,
                reconnectAttempts: this.reconnectAttempts
            },
            'attemptReconnect'
        );
        
        this.emit('connection_status', {
            connected: false,
            status: this.connectionStatus,
            reconnectAttempts: this.reconnectAttempts,
            nextRetryIn: delay
        });
        
        this.reconnectTimer = setTimeout(async () => {
            this.reconnectTimer = null;
            
            try {
                // Try to reconnect with the same parameters
                await this.connect();
                
                ErrorLogger.getInstance().logInfo(
                    'WebSocketService',
                    'Reconnection successful',
                    { reconnectAttempts: this.reconnectAttempts },
                    'attemptReconnect'
                );
                
            } catch (error) {
                ErrorLogger.getInstance().logWarning(
                    'WebSocketService',
                    'Reconnection attempt failed',
                    {
                        error: error instanceof Error ? error.message : String(error),
                        reconnectAttempts: this.reconnectAttempts
                    },
                    'attemptReconnect'
                );
                
                // Try again if we haven't reached max attempts
                if (this.reconnectAttempts < this.maxReconnectAttempts) {
                    this.attemptReconnect();
                } else {
                    this.connectionStatus = 'failed';
                    this.emit('error', { 
                        error: 'Connection lost - manual reconnection required',
                        status: this.connectionStatus,
                        reconnectAttempts: this.reconnectAttempts
                    });
                }
            }
        }, delay);
    }

    isConnected(): boolean {
        return this.ws?.readyState === WebSocket.OPEN;
    }

    /**
     * Get detailed connection status
     */
    getConnectionStatus(): {
        connected: boolean;
        status: string;
        reconnectAttempts: number;
        lastError?: string;
        queuedMessages: number;
    } {
        return {
            connected: this.isConnected(),
            status: this.connectionStatus,
            reconnectAttempts: this.reconnectAttempts,
            lastError: this.lastConnectionError?.message,
            queuedMessages: this.messageQueue.length
        };
    }

    /**
     * Configure validation and sanitization
     */
    setValidationEnabled(enabled: boolean): void {
        this.validationEnabled = enabled;
        ErrorLogger.getInstance().logInfo(
            'WebSocketService',
            `Message validation ${enabled ? 'enabled' : 'disabled'}`,
            {},
            'setValidationEnabled'
        );
    }

    setSanitizationEnabled(enabled: boolean): void {
        this.sanitizationEnabled = enabled;
        ErrorLogger.getInstance().logInfo(
            'WebSocketService',
            `Message sanitization ${enabled ? 'enabled' : 'disabled'}`,
            {},
            'setSanitizationEnabled'
        );
    }

    /**
     * Clear message queue
     */
    clearMessageQueue(): void {
        const queueSize = this.messageQueue.length;
        this.messageQueue = [];
        
        if (queueSize > 0) {
            ErrorLogger.getInstance().logInfo(
                'WebSocketService',
                `Cleared ${queueSize} queued messages`,
                {},
                'clearMessageQueue'
            );
        }
    }

    /**
     * Force reconnection attempt
     */
    forceReconnect(): Promise<void> {
        ErrorLogger.getInstance().logInfo(
            'WebSocketService',
            'Force reconnection requested',
            { currentStatus: this.connectionStatus },
            'forceReconnect'
        );

        // Reset reconnection attempts to allow immediate retry
        this.reconnectAttempts = 0;
        this.connectionStatus = 'disconnected';
        
        // Close existing connection if any
        if (this.ws) {
            this.ws.close(1000, 'Force reconnect');
        }
        
        // Clear any pending reconnection timer
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        
        // Attempt new connection
        return this.connect();
    }

    send(message: any): void {
        try {
            // Validate message before sending
            if (this.validationEnabled) {
                const validation = validateWebSocketMessage(message);
                if (!validation.isValid) {
                    ErrorLogger.getInstance().logWarning(
                        'WebSocketService',
                        'Outgoing message validation failed',
                        {
                            validationErrors: validation.errors,
                            originalData: message
                        },
                        'send'
                    );
                    
                    // Use normalized message if available
                    if (validation.normalizedMessage) {
                        message = validation.normalizedMessage;
                    } else {
                        throw new Error('Cannot send invalid message');
                    }
                }
            }

            // Sanitize message if enabled
            if (this.sanitizationEnabled) {
                message = this.sanitizeMessageData(message);
            }

            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.ws.send(JSON.stringify(message));
                
                ErrorLogger.getInstance().logInfo(
                    'WebSocketService',
                    'Message sent successfully',
                    { messageType: message.type },
                    'send'
                );
            } else {
                // Queue message for later if connection is being established
                if (this.connectionStatus === 'connecting' || this.connectionStatus === 'reconnecting') {
                    this.queueMessage(message);
                    ErrorLogger.getInstance().logInfo(
                        'WebSocketService',
                        'Message queued - connection not ready',
                        {
                            messageType: message.type,
                            connectionStatus: this.connectionStatus
                        },
                        'send'
                    );
                } else {
                    const error = new Error(`WebSocket not connected. Status: ${this.connectionStatus}`);
                    ErrorLogger.getInstance().logWarning(
                        'WebSocketService',
                        error.message,
                        {
                            messageType: message.type,
                            connectionStatus: this.connectionStatus,
                            originalData: message
                        },
                        'send'
                    );
                    throw error;
                }
            }
        } catch (error) {
            ErrorLogger.getInstance().logError(
                'WebSocketService',
                error instanceof Error ? error : new Error(String(error)),
                { originalData: message },
                'send'
            );
            throw error;
        }
    }

    sendPlanApprovalResponse(response: {
        plan_id: string;
        session_id: string;
        approved: boolean;
        feedback?: string;
        user_response?: string;
        human_clarification?: string;
    }): void {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            this.emit('error', { error: 'Cannot send plan approval response - WebSocket not connected' });
            return;
        }
        try {
            const v3Response = {
                m_plan_id: response.plan_id,
                approved: response.approved,
                feedback: response.feedback || response.user_response || response.human_clarification || '',
            };
            const message = {
                type: WebsocketMessageType.PLAN_APPROVAL_RESPONSE,
                data: v3Response
            };
            this.ws.send(JSON.stringify(message));
        } catch {
            this.emit('error', { error: 'Failed to send plan approval response' });
        }
    }

    /**
     * Validate agent message data structure
     */
    private validateAgentMessageData(data: any): boolean {
        if (!data || typeof data !== 'object') {
            return false;
        }

        // Check for required fields in various formats using safe property access
        const agentName = typeSafeAccess.string(data, 'agent_name', '') || typeSafeAccess.string(data, 'agent', '');
        const content = typeSafeAccess.string(data, 'content', '');
        const hasAgentName = agentName.trim() !== '';
        // Content can be empty for progress messages - only require agent name
        // The renderAgentMessages function will filter out empty content messages if needed
        
        return hasAgentName;
    }

    /**
     * Validate streaming message data structure
     */
    private validateStreamingMessageData(data: any): boolean {
        if (!data || typeof data !== 'object') {
            return false;
        }

        // Check for required fields in streaming messages using safe property access
        const agentName = typeSafeAccess.string(data, 'agent_name', '') || typeSafeAccess.string(data, 'agent', '');
        const content = typeSafeAccess.string(data, 'content', '');
        const isFinal = typeSafeAccess.boolean(data, 'is_final', false);
        const hasAgentName = agentName.trim() !== '';
        const hasContent = content.trim() !== '';
        const hasIsFinal = typeof isFinal === 'boolean';
        
        return hasAgentName && hasContent && hasIsFinal;
    }
}

export const webSocketService = new WebSocketService();
export default webSocketService;